import { useCallback, useEffect, useRef, useState } from "react";
import { callQuery, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE, GET_LATEST_VERSIONS_FOR_CSN, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PatientFileVersion, UNREAD_EXPLAIN_JOBS } from "../../../../amplify/appsync/query";
import { PatientVisit } from "../../../../amplify/appsync/search-patients";

import { v4 as uuidv4 } from 'uuid';
import { callLLMDashboardStreamable } from "../../../../amplify/api/llm-chat";
import { getWSService } from "../../../../amplify/api/llm-web-socket";
import { callDashboardLLM, callGetDashboardLLMStatus, LLMDashboardStatus } from "../../../../amplify/appsync/dashboard-llm";
import { callWrite, EXPLAIN_CURRENT_VISIT, EXPLAIN_MONITOR, EXPLAIN_PATIENT_SUMMARY, SCAN_MULTI, UPDATE_LAST_VIEWED } from "../../../../amplify/appsync/write";
import { useDashboardTrigger } from "../../../../context/DashboardTriggerContext";
import { dashboardLLMCache } from "../cache";
import { AdditionalContextModal } from "../common/AdditionalContextModal";
import { ScanMultiModal } from "../common/ScanMultiModal";
import './NotesDashboard.scss';
import { SectionCurrentVisit } from "./SectionCurrentVisit";
import { SectionMonitoringSummary } from "./SectionMonitoringSummary";
import { SectionPatientSummary } from "./SectionPatientSummary";
import { jsonrepair } from "jsonrepair";
import { getFileFromKey } from "../common/utils";


export const JOB_CONFIG = [
    {
        key: "current_visit_presentation",
        file: EXPLAIN_CURRENT_VISIT,
    },
    {
        key: "patient_summary",
        file: EXPLAIN_PATIENT_SUMMARY,
    },
    {
        key: "monitor_summary",
        file: EXPLAIN_MONITOR,
    },
];

let socketConnection: any;
let sessionId: string;

export const MonitorDashboard = (props: {
    user: any,
    patientVisit: PatientVisit,
    ws: string,
    tenant: string,
}) => {
    const [statuses, setStatuses] = useState<Record<string, LLMDashboardStatus>>({});

    const [feedbacks, setFeedbacks] = useState<LLMVisitFeedback[]>([]);
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [unreadFileVersionMap, setUnreadFileVersionMap] = useState<Record<string, PatientFileVersion>>();

    //
    // State variables for the scan multi popup
    //
    const [isScanMultiModalOpen, setIsScanMultiModalOpen] = useState(false);
    const [latestFileVersions, setLatestFileVersions] = useState<Record<string, PatientFileVersion>>();
    const [selectedScanMultiVersion, setSelectedScanMultiVersion] = useState<any>();
    const [selectedInitialSection, setSelectedInitialSection] = useState<string>();
    const [selectedTriggeringEvents, setSelectedTriggeringEvents] = useState<string[]>();

    //
    // State variables for the pre-computed data
    //
    const [currentVisitPresentation, setCurrentVisitPresentation] = useState<any>();
    const [patientSummary, setPatientSummary] = useState<any>();
    const [monitorSummary, setMonitorSummary] = useState<any>();

    // 
    // Preparing environment
    // 
    const regenerateKeyRef = useRef<any>();
    const [isWebSocketConnecting, setIsWebSocketConnecting] = useState<any>(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateTarget, setRegenerateTarget] = useState<string | undefined>();

    const [isLoadingCurrentVisitPresentation, setIsLoadingCurrentVisitPresentation] = useState<boolean>(false);
    const [isLoadingPatientSummary, setIsLoadingPatientSummary] = useState<boolean>(false);
    const [isLoadingMonitorSummary, setIsLoadingMonitorSummary] = useState<boolean>(false);

    const sectionToSetIsLoading: { [key: string]: (val: any) => void } = {
        current_visit_presentation: setIsLoadingCurrentVisitPresentation,
        patient_summary: setIsLoadingPatientSummary,
        monitor_summary: setIsLoadingMonitorSummary,
    };

    const sectionToSetter: { [key: string]: (val: any) => void } = {
        current_visit_presentation: setCurrentVisitPresentation,
        patient_summary: setPatientSummary,
        monitor_summary: setMonitorSummary,
    };

    const { triggerAction, setTriggerAction } = useDashboardTrigger();

    const loadPrecomputedExplanations = async (patient: PatientVisit) => {
        const unavailableExplanations: string[] = [];

        const sections = JOB_CONFIG;

        for (let section of sections) {
            sectionToSetIsLoading[section.key]?.(true);
            const cachedCurrentVisit = await dashboardLLMCache.get(section.file, patient, props.tenant);
            if (cachedCurrentVisit) {
                sectionToSetter[section.key]?.(cachedCurrentVisit);
            } else {
                unavailableExplanations.push();
            }
            sectionToSetIsLoading[section.key]?.(false);
        }
    };

    useEffect(() => {
        if (props.patientVisit) {
            let currPatientVisit = props.patientVisit;

            loadPrecomputedExplanations(currPatientVisit);

            callGetDashboardLLMStatus(currPatientVisit.csn).then((statuses) => {
                setStatuses(statuses);
            });

            callQuery({
                operation: GET_LATEST_VERSIONS_FOR_CSN,
                csn: currPatientVisit.csn,
            }).then((latestVersions: PatientFileVersion[]) => {
                const fileTypeToVersion: any = {};
                for (let latestVersion of latestVersions) {
                    fileTypeToVersion[latestVersion.file_type] = latestVersion;
                }
                setLatestFileVersions(fileTypeToVersion);
            });

            callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn: currPatientVisit.csn,
            }).then(feedbacks => {
                setFeedbacks(feedbacks);
            });

            callQuery({
                operation: UNREAD_EXPLAIN_JOBS,
                csn: props.patientVisit.csn,
            }).then((unreadFileVersions: PatientFileVersion[]) => {
                const fileVersionMap: Record<string, PatientFileVersion> = {};
                for (let fv of unreadFileVersions) {
                    fileVersionMap[fv.file_type] = fv;
                }
                setUnreadFileVersionMap(fileVersionMap);
    
                callWrite({
                    operation: UPDATE_LAST_VIEWED,
                    csn: props.patientVisit.csn,
                    jobs: JOB_CONFIG.map(config => config.file),
                }).then((result: any) => {
                    console.log("Completed UPDATE_LAST_VIEWED");
                });
            });
        }
    }, [props.patientVisit]);

    // Update the ref whenever regenerateTarget changes
    useEffect(() => {
        let config = null;
        for (let i = 0; i < JOB_CONFIG.length; i++) {
            if (regenerateTarget && JOB_CONFIG[i].file !== regenerateTarget) {
                continue;
            }
            config = JOB_CONFIG[i];
        }
        regenerateKeyRef.current = config?.key;
    }, [regenerateTarget]);

    // Store accumulated text in a ref so we don't trigger extra re-renders
    const accumulatedTextRef = useRef("");
    const lastSeqRef = useRef(0);

    const handleChatUpdate = useCallback((response: any) => {
        if (
            response &&
            typeof response === "object" &&
            !Array.isArray(response) &&
            (response["role"] === "COMPLETED" || response["role"] === "COMPLETED_WITH_ERROR")
        ) {
            // End of stream cleanup
            if (props.patientVisit && regenerateKeyRef.current) {
                callQuery({
                    operation: GET_LATEST_VERSIONS_FOR_CSN,
                    csn: props.patientVisit.csn,
                }).then((latestVersions: PatientFileVersion[]) => {
                    const fileTypeToVersion: any = {};
                    for (let latestVersion of latestVersions) {
                        fileTypeToVersion[latestVersion.file_type] = latestVersion;
                    }
                    setLatestFileVersions(fileTypeToVersion);
                });

                dashboardLLMCache.get(getFileFromKey(JOB_CONFIG, regenerateKeyRef.current), props.patientVisit, props.tenant).then((cachedCurrentVisit) => {
                    sectionToSetter[regenerateKeyRef.current]?.(cachedCurrentVisit);
                    sectionToSetIsLoading[regenerateKeyRef.current](false);
                });
                callGetDashboardLLMStatus(props.patientVisit.csn).then((statuses) => {
                    setStatuses(statuses);
                });
            }
        }
        // Oct 2025: Disable streaming because time-to-first token is high for Azure OpenAI models
        //           which leads to a degraded/non-existent stream. Re-enable once we move back to 
        //           native OpenAI or Claude models.
        //
        // else if (response && typeof response === "object" && "seq" in response && "delta" in response) {
        //     const { seq, delta } = response;

        //     // Optional: Check for skipped or out-of-order chunks
        //     if (seq !== lastSeqRef.current + 1) {
        //         console.warn(`⚠ Out of order chunk: expected ${lastSeqRef.current + 1} but got ${seq}`);
        //         // Optionally handle gap recovery here
        //     }

        //     // Append the new delta to accumulated text
        //     accumulatedTextRef.current += delta;
        //     lastSeqRef.current = seq;

        //     // Push the updated text into your UI
        //     if (regenerateKeyRef.current) {
        //         const key: any = regenerateKeyRef.current;
        //         sectionToSetIsLoading[key](false);
        //         try {
        //             sectionToSetter[key](JSON.parse(jsonrepair(accumulatedTextRef.current)));
        //         } catch {
        //             console.warn(`Could not parse the following accumulated text: ${accumulatedTextRef.current}`);
        //             sectionToSetter[key]({});
        //         }
        //     }
        // }
        // else if (regenerateKeyRef.current) {
        //     // Fallback: if the backend sends a plain string (legacy mode)
        //     const key: any = regenerateKeyRef.current;
        //     sectionToSetIsLoading[key](false);
        //     sectionToSetter[key](response);
        // }
    }, [regenerateTarget]);

    const handleWebSocketConnected = useCallback(() => {
        setIsWebSocketConnecting(false);
    }, []);

    const connect = () => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;

        setIsWebSocketConnecting(true);
        sessionId = uuidv4();
        socketConnection = getWSService(props.ws, sessionId, handleWebSocketConnected);
        if (socketConnection.sessionId) {
            // This is because getWSService may actually return a previous open web socket with an older connection ID
            sessionId = socketConnection.sessionId;
        }

        if (!socketConnection.hasListener(handleChatUpdate)) {
            socketConnection.addMessageListener(handleChatUpdate);
        }
    }

    const regenerate = async (shouldSyncData: boolean, target?: string) => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;
        if (props.patientVisit) {
            setIsRegenerating(true);

            let config = null;

            for (let i = 0; i < JOB_CONFIG.length; i++) {
                if (target && JOB_CONFIG[i].file !== target) {
                    continue;
                }
                if (JOB_CONFIG[i].file.startsWith("scan_")) {
                    continue;
                }
                config = JOB_CONFIG[i];
            }

            connect();

            if (config) {
                sectionToSetIsLoading[config.key]?.(true);
            }

            if (shouldSyncData) {
                const scanMultiResponse = await callDashboardLLM({
                    "operation": SCAN_MULTI,
                    "mrn": props.patientVisit.mrn,
                    "csn": props.patientVisit.csn,
                });
            }
            if (config) {
                await callLLMDashboardStreamable(sessionId, {
                    operation: config.file,
                    mrn: props.patientVisit.mrn,
                    csn: props.patientVisit.csn,
                    report_date_str: new Date().toISOString().split('.')[0] + 'Z',
                });
            }
            setIsRegenerating(false);
        }
    };

    useEffect(() => {
        if (triggerAction && triggerAction.name === 'overview') {
            regenerate(true);
            // Important: Reset the trigger action to avoid re-triggering
            setTriggerAction(null);
        }
    }, [triggerAction, regenerate, setTriggerAction]);


    /**
     * Called when the user wants to see the data snapshot for the LLM section
     */
    const handleShowScanMultiModalForSection = (patientFileVersion?: PatientFileVersion) => {
        if (patientFileVersion) {
            callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patientFileVersion.csn,
                file_type: 'scan_multi',
            })
            .then((versions: PatientFileVersion[]) => {
                // Find previous version
                const sortedVersions = [...versions].sort((a, b) => new Date(a.version_date).getTime() - new Date(b.version_date).getTime());
                const selectedIndex = sortedVersions.findIndex(v => v.version_id === patientFileVersion.source_version_id);
                const previousVersion = selectedIndex > 0 ? sortedVersions[selectedIndex - 1] : null;

                setSelectedScanMultiVersion({
                    origVersion: previousVersion ? previousVersion.source_version_id : undefined,
                    newVersion: patientFileVersion.source_version_id,
                });
            });
            setSelectedTriggeringEvents(patientFileVersion.source_changed_keys);
            setIsScanMultiModalOpen(true);
        }
    };

    /**
     * Called when the user selects another version of the LLM section
     */
    const handleFileVersionChange = (fileType: string, fileVersion: PatientFileVersion, previousFileVersion: PatientFileVersion | null) => {
        const updatedFileVersions = {
            ...latestFileVersions,
            [fileType]: {
                ...fileVersion
            },
        };
        setSelectedScanMultiVersion({
            origVersion: previousFileVersion ? previousFileVersion.source_version_id : undefined,
            newVersion: fileVersion.source_version_id,
        })
        setLatestFileVersions(updatedFileVersions);
    }

    return (
        <div className="notes-dashboard">
            <SectionPatientSummary className="mb-2" isLoadingPatientSummary={isLoadingPatientSummary} patientSummary={patientSummary} patientVisit={props.patientVisit} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} user={props.user} tenant={props.tenant} />
            <SectionCurrentVisit className="mb-2" isLoadingCurrentVisitPresentation={isLoadingCurrentVisitPresentation} currentVisitPresentation={currentVisitPresentation} patientVisit={props.patientVisit} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} user={props.user} tenant={props.tenant} />
            <SectionMonitoringSummary className="mb-2" isLoadingMonitoringSummary={isLoadingMonitorSummary} monitoringSummary={monitorSummary} patientVisit={props.patientVisit} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} tenant={props.tenant} />
            <AdditionalContextModal regenerate={regenerate} showModal={showContextMenu} setShowModal={setShowContextMenu} patientVisit={props.patientVisit} target={regenerateTarget} tenant={props.tenant} />
            {
                props.patientVisit && (
                    <ScanMultiModal patient={props.patientVisit} isOpen={isScanMultiModalOpen} onClose={() => {setIsScanMultiModalOpen(false)}} versionInfo={selectedScanMultiVersion} initialSection={selectedInitialSection} triggeringEvents={selectedTriggeringEvents} />
                )
            }
        </div>
    );
};
